'use client';

import { useDrag } from '@use-gesture/react';
import {
  type CSSProperties,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

interface UseSheetDragProps {
  isBottomSheet: boolean;
  isMobileSize: boolean;
  contentRef: React.RefObject<HTMLDivElement | null>;
  style?: CSSProperties;
}

interface UseSheetDragReturn {
  sheetMotionStyle: CSSProperties | undefined;
  dragBinds: ReturnType<typeof useDrag>;
  shouldRenderDragHandle: boolean;
}

const DISMISS_VELOCITY_Y = 0.6;

const useSheetDrag = ({
  isBottomSheet,
  isMobileSize,
  contentRef,
  style,
}: UseSheetDragProps): UseSheetDragReturn => {
  const shouldEnableDrag = isBottomSheet && isMobileSize;

  const [dragY, setDragY] = useState(0);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isSheetDragging, setIsSheetDragging] = useState(false);
  const [shouldReduceMotion, setShouldReduceMotion] = useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  });
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const dragYRef = useRef(0);

  useLayoutEffect(() => {
    dragYRef.current = dragY;
  }, [dragY]);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const handleChange = (e: MediaQueryListEvent) => setShouldReduceMotion(e.matches);
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  useEffect(() => {
    if (!isBottomSheet || !contentRef.current) return;

    const handleStateChange = () => {
      const state = contentRef.current?.getAttribute('data-state');
      const isNowOpen = state === 'open';
      setIsDialogOpen(isNowOpen);

      if (isNowOpen) {
        dragYRef.current = 0;
        setDragY(0);
        setIsExpanded(false);
      }
    };

    const observer = new MutationObserver(handleStateChange);
    observer.observe(contentRef.current, { attributes: true, attributeFilter: ['data-state'] });

    handleStateChange();

    return () => observer.disconnect();
  }, [isBottomSheet, contentRef]);

  const handleDismiss = useCallback(() => {
    if (contentRef.current) {
      const dialog = contentRef.current.closest('[role="dialog"]');
      if (dialog) {
        // Dispatch Escape keydown event to trigger Radix UI Dialog's dismiss handler
        // This is the standard approach for programmatically closing Radix dialogs
        const closeEvent = new KeyboardEvent('keydown', {
          key: 'Escape',
          code: 'Escape',
          bubbles: true,
          cancelable: true,
        });
        contentRef.current.dispatchEvent(closeEvent);
      }
    }
  }, [contentRef]);

  const bindDragHandle = useDrag(
    ({ down: isPointerDown, movement: [, my], velocity: [, vy] }) => {
      if (!isBottomSheet) return 0;

      if (!isPointerDown) {
        setIsSheetDragging(false);

        if (my > 0) {
          const dismissDistancePx = Math.min(window.innerHeight * 0.22, 200);
          if (isExpanded) {
            const collapseTriggerPx = 50;
            if (my > collapseTriggerPx) setIsExpanded(false);
          } else {
            const shouldDismiss = my > dismissDistancePx || (my > 52 && vy > DISMISS_VELOCITY_Y);
            if (shouldDismiss) {
              handleDismiss();
              return 0;
            }
          }
        }

        if (my < 0) setIsExpanded(true);

        dragYRef.current = 0;
        setDragY(0);
        return 0;
      }

      if (Math.abs(my) > 2) setIsSheetDragging(true);

      if (my > 0) {
        setDragY(my);
        dragYRef.current = my;
      } else if (my < 0) {
        if (!isExpanded) setIsExpanded(true);
      }

      return my;
    },
    {
      axis: 'y',
      filterTaps: true,
      pointer: { capture: false },
      from: () => [0, dragYRef.current],
      enabled: shouldEnableDrag && typeof window !== 'undefined',
    }
  );

  const sheetMotionStyle = useMemo(() => {
    if (!isBottomSheet) return style;

    const motionStyle: CSSProperties & { '--bg'?: string } = {
      ...style,
      maxHeight: isExpanded ? '100dvh' : '90dvh',
      transform: isDialogOpen ? `translate3d(0, ${dragY}px, 0)` : undefined,
      transition:
        shouldReduceMotion || isSheetDragging
          ? 'none'
          : 'transform 0.22s cubic-bezier(0.32, 0.72, 0, 1)',
      willChange: isSheetDragging ? 'transform' : 'auto',
    };
    return motionStyle;
  }, [dragY, isExpanded, isSheetDragging, shouldReduceMotion, isBottomSheet, style, isDialogOpen]);

  return {
    sheetMotionStyle,
    dragBinds: bindDragHandle,
    shouldRenderDragHandle: shouldEnableDrag,
  };
};

export { useSheetDrag };
export type { UseSheetDragProps, UseSheetDragReturn };
